import { useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { ArrowRight, Satellite, Eye, DollarSign, Zap } from "lucide-react"
import { useAutocomplete, getSatelliteImageUrl } from "@/hooks/useGoogleMaps"
import { useWizardState } from "@/hooks/useWizardState"

const CDN = "https://client-assets.maymarketingseo.com/skyline-roofing"

export default function Home() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [hasAddress, setHasAddress] = useState(false)
  const [addressData, setAddressData] = useState<{
    address: string
    formattedAddress: string
    lat: number
    lng: number
  } | null>(null)

  useAutocomplete(inputRef, (place) => {
    setAddressData(place)
    setHasAddress(true)
  })

  const handleGetPrice = () => {
    if (!addressData) return
    // Store in sessionStorage so the wizard can pick it up
    sessionStorage.setItem("estimator_address", JSON.stringify(addressData))
    navigate("/estimate")
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gradient-to-b from-white via-red-50/20 to-red-50/40 flex flex-col">
      <main className="flex-1 px-6 lg:px-10 pt-10 md:pt-16 pb-16 max-w-7xl mx-auto w-full">
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-16">
          {/* Left column — text + address card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex-1 max-w-xl"
          >
            {/* Pill badge */}
            <div className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/5 rounded-full px-4 py-1.5 mb-6">
              <Zap className="w-3.5 h-3.5 text-primary" />
              <span className="text-[13px] font-semibold text-primary">
                Instant Roofing Estimates
              </span>
            </div>

            {/* Headlines */}
            <h1 className="text-[32px] md:text-[44px] font-extrabold leading-[1.1] mb-2">
              <span className="text-primary">Skyline Roofing</span>
            </h1>
            <h2 className="text-[28px] md:text-[36px] font-extrabold text-gray-900 leading-[1.15] mb-1">
              From Estimation to Installation
            </h2>
            <p className="text-xl md:text-2xl font-semibold text-gray-400 mb-1">
              Know Your Cost Before We Show Up
            </p>
            <p className="text-lg font-bold text-primary mb-6">
              Cincinnati &bull; Louisville &bull; Lexington
            </p>
            <p className="text-gray-500 text-base leading-relaxed mb-8 max-w-md">
              Get instant pricing, explore real materials, and schedule
              installation — all with full transparency and zero sales calls.
            </p>

            {/* Address card */}
            <div className="bg-white rounded-2xl p-5 shadow-lg shadow-gray-200/40 border border-gray-100 mb-4">
              <div className="text-[10px] tracking-[1.5px] font-bold text-gray-400 uppercase mb-2">
                Home Address
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="123 Main St, Dayton OH"
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl font-medium text-gray-900 text-[15px] outline-none focus:border-primary transition-colors placeholder:text-gray-400"
                    aria-label="Address autocomplete input"
                  />
                </div>
                <button
                  onClick={handleGetPrice}
                  disabled={!hasAddress}
                  className={`px-6 py-3.5 rounded-xl font-bold text-[15px] whitespace-nowrap transition-all ${
                    hasAddress
                      ? "bg-primary text-white hover:bg-primary-dark hover:shadow-lg hover:shadow-primary/25"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  Get My Price <ArrowRight className="w-4 h-4 inline ml-1" />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-6">
              Takes less than 2 minutes &bull; No sales pressure
            </p>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <div className="flex items-center gap-1.5">
                <Satellite className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-gray-600">
                  Real satellite measurement
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Eye className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-gray-600">
                  No home visit needed
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <DollarSign className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-gray-600">
                  Transparent pricing
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right column — overlapping photos (desktop only) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-1 max-w-lg w-full hidden md:block"
          >
            <div className="relative" style={{ minHeight: 480 }}>
              {/* Top-left large image */}
              <div className="absolute top-0 left-0 w-[65%] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={`${CDN}/gallery/gallery-001.png`}
                  alt="Professional roof installation"
                  className="w-full h-64 object-cover"
                  loading="lazy"
                />
              </div>
              {/* Top-right tilted image */}
              <div
                className="absolute top-0 right-0 w-[42%] rounded-2xl overflow-hidden shadow-xl"
                style={{ transform: "rotate(3deg)" }}
              >
                <img
                  src={`${CDN}/gallery/gallery-003.png`}
                  alt="Completed roofing project"
                  className="w-full h-48 object-cover"
                  loading="lazy"
                />
              </div>
              {/* Bottom center image */}
              <div className="absolute bottom-0 left-[10%] w-[60%] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={`${CDN}/gallery/gallery-005.png`}
                  alt="Beautiful home with new Skyline roof"
                  className="w-full h-56 object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </motion.div>

          {/* Mobile photo row */}
          <div className="w-full md:hidden">
            <div className="flex gap-3 overflow-hidden rounded-2xl">
              <div className="flex-1 rounded-2xl overflow-hidden shadow-md">
                <img
                  src={`${CDN}/gallery/gallery-001.png`}
                  alt="Professional roof installation"
                  className="w-full h-40 object-cover"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 rounded-2xl overflow-hidden shadow-md">
                <img
                  src={`${CDN}/gallery/gallery-003.png`}
                  alt="Completed roofing project"
                  className="w-full h-40 object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
